import React, { useEffect, useState } from 'react';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import ComponentCard from '../../components/common/ComponentCard';
import PageMeta from '../../components/common/PageMeta';
import { Button, Label, TextInput } from 'flowbite-react';
import { useChurchInfoStore } from '../../store/useChurchInfoStore';
import { ServiceTime, SocialLink } from '../../api/api';
import { useCanAccessResource, useHasPermission, useIsMainAdmin } from '../../utils/permissions';
import { usePermissionStore } from '../../store/usePermissionStore';
import AccessDenied from '../../components/common/AccessDenied';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SOCIAL_PLATFORMS = ['Facebook', 'Instagram', 'Twitter', 'YouTube', 'LinkedIn', 'WhatsApp', 'Other'];

export default function ChurchInfoPage() {
  const getChurchInfo = useChurchInfoStore((state) => state.getChurchInfo);
  const updateChurchInfo = useChurchInfoStore((state) => state.updateChurchInfo);
  const churchInfo = useChurchInfoStore((state) => state.churchInfo);
  const loading = useChurchInfoStore((state) => state.loading);

  const canAccess = useCanAccessResource('church-info');
  const hasReadPermission = useHasPermission('church-info', 'read');
  const hasUpdatePermission = useHasPermission('church-info', 'update');
  const isMainAdmin = useIsMainAdmin();
  const isLoadingPermissions = usePermissionStore((state) => state.isLoading);
  const hasFetched = usePermissionStore((state) => state.hasFetched);

  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [serviceTimes, setServiceTimes] = useState<ServiceTime[]>([]);
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);

  useEffect(() => {
    if (hasFetched && !isLoadingPermissions && (hasReadPermission || isMainAdmin)) {
      getChurchInfo();
    }
  }, [getChurchInfo, hasReadPermission, isMainAdmin, hasFetched, isLoadingPermissions]);

  useEffect(() => {
    if (churchInfo) {
      setAddress(churchInfo.address || '');
      setPhone(churchInfo.phone || '');
      setEmail(churchInfo.email || '');
      setServiceTimes(churchInfo.serviceTimes || []);
      setSocialLinks(churchInfo.socialLinks || []);
    }
  }, [churchInfo]);

  const handleSave = async () => {
    await updateChurchInfo({
      address,
      phone,
      email,
      serviceTimes,
      socialLinks,
    });
  };

  const handleServiceTimeChange = (index: number, field: keyof ServiceTime, value: string) => {
    const updated = [...serviceTimes];
    updated[index] = { ...updated[index], [field]: value };
    setServiceTimes(updated);
  };

  const addServiceTime = () => {
    setServiceTimes([...serviceTimes, { day: 'Sunday', time: '', description: '' }]);
  };

  const removeServiceTime = (index: number) => {
    setServiceTimes(serviceTimes.filter((_, i) => i !== index));
  };

  const handleSocialLinkChange = (index: number, field: keyof SocialLink, value: string) => {
    const updated = [...socialLinks];
    updated[index] = { ...updated[index], [field]: value } as SocialLink;
    setSocialLinks(updated);
  };

  const addSocialLink = () => {
    setSocialLinks([...socialLinks, { platform: 'Facebook', url: '' }]);
  };

  const removeSocialLink = (index: number) => {
    setSocialLinks(socialLinks.filter((_, i) => i !== index));
  };

  if (!hasFetched || isLoadingPermissions) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B8860B] mx-auto"></div>
          <p className="mt-4 text-gray-600" style={{ fontFamily: 'Lato, sans-serif' }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!canAccess && !isMainAdmin) {
    return <AccessDenied resourceName="Church Information" />;
  }

  if (!hasReadPermission && !isMainAdmin) {
    return <AccessDenied resourceName="Church Information" />;
  }

  return (
    <>
      <PageMeta title="Church Information - Church Management System" description="Manage church contact information and service times" />
      <PageBreadcrumb pageTitle="Church Information" />

      <ComponentCard title="Church Information Management">
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Manage Church Information
            </h2>
            <p className="text-gray-600" style={{ fontFamily: 'Lato, sans-serif' }}>
              Edit the church contact information and service times. This information will be displayed on the public website.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="address" value="Address" className="mb-2" style={{ fontFamily: 'Lato, sans-serif' }} />
              <TextInput
                id="address"
                type="text"
                placeholder="123 Faith Street, City, State 12345"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
            </div>

            <div>
              <Label htmlFor="phone" value="Phone" className="mb-2" style={{ fontFamily: 'Lato, sans-serif' }} />
              <TextInput
                id="phone"
                type="text"
                placeholder="(555) 123-4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
            </div>

            <div>
              <Label htmlFor="email" value="Email" className="mb-2" style={{ fontFamily: 'Lato, sans-serif' }} />
              <TextInput
                id="email"
                type="email"
                placeholder="info@stantonyschurch.org"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <Label htmlFor="serviceTimes" value="Service Times" className="mb-2" style={{ fontFamily: 'Lato, sans-serif' }} />
                <Button size="sm" onClick={addServiceTime} className="bg-[#B8860B] hover:bg-[#9A7209]" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Add Service Time
                </Button>
              </div>

              <div className="space-y-4">
                {serviceTimes.map((serviceTime, index) => (
                  <div key={index} className="flex gap-4 items-end p-4 border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <Label htmlFor={`day-${index}`} value="Day" className="mb-2" style={{ fontFamily: 'Lato, sans-serif' }} />
                      <select
                        id={`day-${index}`}
                        value={serviceTime.day}
                        onChange={(e) => handleServiceTimeChange(index, 'day', e.target.value)}
                        className="block w-full rounded-lg border-gray-300 bg-gray-50 text-gray-900 focus:border-[#B8860B] focus:ring-[#B8860B]"
                        style={{ fontFamily: 'Lato, sans-serif' }}
                      >
                        {DAYS_OF_WEEK.map((day) => (
                          <option key={day} value={day}>
                            {day}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1">
                      <Label htmlFor={`time-${index}`} value="Time" className="mb-2" style={{ fontFamily: 'Lato, sans-serif' }} />
                      <TextInput
                        id={`time-${index}`}
                        type="text"
                        placeholder="10:00 AM"
                        value={serviceTime.time}
                        onChange={(e) => handleServiceTimeChange(index, 'time', e.target.value)}
                        style={{ fontFamily: 'Lato, sans-serif' }}
                      />
                    </div>
                    <div className="flex-1">
                      <Label
                        htmlFor={`description-${index}`}
                        value="Description (Optional)"
                        className="mb-2"
                        style={{ fontFamily: 'Lato, sans-serif' }}
                      />
                      <TextInput
                        id={`description-${index}`}
                        type="text"
                        placeholder="Bible Study"
                        value={serviceTime.description || ''}
                        onChange={(e) => handleServiceTimeChange(index, 'description', e.target.value)}
                        style={{ fontFamily: 'Lato, sans-serif' }}
                      />
                    </div>
                    <Button color="failure" size="sm" onClick={() => removeServiceTime(index)} style={{ fontFamily: 'Lato, sans-serif' }}>
                      Remove
                    </Button>
                  </div>
                ))}
                {serviceTimes.length === 0 && (
                  <p className="text-gray-500 text-sm" style={{ fontFamily: 'Lato, sans-serif' }}>
                    No service times added. Click "Add Service Time" to add one.
                  </p>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <Label htmlFor="socialLinks" value="Social Media Links" className="mb-2" style={{ fontFamily: 'Lato, sans-serif' }} />
                <Button size="sm" onClick={addSocialLink} className="bg-[#B8860B] hover:bg-[#9A7209]" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Add Social Link
                </Button>
              </div>

              <div className="space-y-4">
                {socialLinks.map((socialLink, index) => (
                  <div key={index} className="flex gap-4 items-end p-4 border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <Label htmlFor={`platform-${index}`} value="Platform" className="mb-2" style={{ fontFamily: 'Lato, sans-serif' }} />
                      <select
                        id={`platform-${index}`}
                        value={socialLink.platform}
                        onChange={(e) => handleSocialLinkChange(index, 'platform', e.target.value)}
                        className="block w-full rounded-lg border-gray-300 bg-gray-50 text-gray-900 focus:border-[#B8860B] focus:ring-[#B8860B]"
                        style={{ fontFamily: 'Lato, sans-serif' }}
                      >
                        {SOCIAL_PLATFORMS.map((platform) => (
                          <option key={platform} value={platform}>
                            {platform}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-2">
                      <Label htmlFor={`url-${index}`} value="URL" className="mb-2" style={{ fontFamily: 'Lato, sans-serif' }} />
                      <TextInput
                        id={`url-${index}`}
                        type="url"
                        placeholder="https://facebook.com/yourpage"
                        value={socialLink.url}
                        onChange={(e) => handleSocialLinkChange(index, 'url', e.target.value)}
                        style={{ fontFamily: 'Lato, sans-serif' }}
                      />
                    </div>
                    <Button color="failure" size="sm" onClick={() => removeSocialLink(index)} style={{ fontFamily: 'Lato, sans-serif' }}>
                      Remove
                    </Button>
                  </div>
                ))}
                {socialLinks.length === 0 && (
                  <p className="text-gray-500 text-sm" style={{ fontFamily: 'Lato, sans-serif' }}>
                    No social links added. Click "Add Social Link" to add one.
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              {(hasUpdatePermission || isMainAdmin) && (
                <Button
                  onClick={handleSave}
                  disabled={loading}
                  className="bg-[#B8860B] hover:bg-[#9A7209]"
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </ComponentCard>
    </>
  );
}
